import React from 'react';
import PropTypes from 'prop-types';
import useDefenderActions from '../../../hooks/useDefenderActions';
import { AUDIT_CHANGE_DEVICE_BLOCKED } from '../../../hooks/useAuditLog';

/**
 * Format action type for display with appropriate styling
 */
const ActionTypeBadge = ({ changeType }) => {
  const isBlocked = changeType === AUDIT_CHANGE_DEVICE_BLOCKED;
  const label = isBlocked ? 'Blocked' : 'Unblocked';
  const colorClasses = isBlocked
    ? 'bg-red-100 text-red-800'
    : 'bg-green-100 text-green-800';

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses}`}>
      {label}
    </span>
  );
};

ActionTypeBadge.propTypes = {
  changeType: PropTypes.string.isRequired,
};

/**
 * Format timestamp for display
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * Loading spinner component
 */
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
    <p className="ml-2 text-gray-700">Loading defender actions...</p>
  </div>
);

/**
 * Empty state component
 */
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
    <svg
      className="w-16 h-16 text-gray-300 mb-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
    <span className="text-lg font-medium">No Defender Actions Recorded</span>
    <span className="text-sm mt-1">
      No block or unblock actions have been recorded for this device.
    </span>
  </div>
);

/**
 * Error state component
 */
const ErrorState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md">
      <div className="flex items-center">
        <svg
          className="w-6 h-6 text-red-500 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="text-red-700 font-medium">Error loading defender actions</span>
      </div>
      <p className="mt-2 text-sm text-red-600">{message}</p>
    </div>
  </div>
);

ErrorState.propTypes = {
  message: PropTypes.string.isRequired,
};

/**
 * DefenderActionsTab - Displays audit history of block/unblock actions for a device
 */
export default function DefenderActionsTab({ deviceSerial }) {
  const { data, isLoading, isError, error } = useDefenderActions(deviceSerial);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorState message={error?.message || 'Unknown error occurred'} />;
  }

  if (!data || data.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4 p-4">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Type
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Timestamp
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                User
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Reason
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((action) => (
              <tr key={action.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <ActionTypeBadge changeType={action.change} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatTimestamp(action.action_timestamp)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {action.user_name || action.user_id || '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate">
                  {action.detail || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

DefenderActionsTab.propTypes = {
  deviceSerial: PropTypes.string.isRequired,
};
